import { JSX, useImperativeHandle, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import Autocomplete, { type IAutocomplete, type IAutocompleteHistoryProps } from '../AutocompleteBase';
import type { IAutocompleteItem } from '../../../models/autocomplete-item.model';
import { MultiFacetVersionedLineApiModel, MultiFacetMunicipalityApiModel, MultiFacetAutocompleteApiModel } from './models/multi-facet-autocomplete.api.model';
import type { ILine } from '../../../models/line.model';
import { TranslationService } from '../../../services/translation.service';
import ValidationInfo from '../../../validation/validation-info';
import RequiredAsterisk from '../RequiredAsterisk';
import ValidationEngine from '../../../validation/validation-engine';
import type { IServerValidationError } from '../validatable.interface';
import { AutocompleteLabelPosition, AutocompleteSuggestionSource } from '../AutocompleteBase/autocomplete-base.enum';
import type IMultiFacet from '../../../models/multi-facet.model';
import { MultiFacetType } from '../../../models/multi-facet-type.enum';
import { MultiFacetHelper } from '../../../helpers/multi-facet.helper';
import MunicipalityListShowcase from '../MunicipalityListShowcase';
import { VersionedLineHelper } from '../../../helpers/versioned-line.helper';
import LineDescription from '../LineDescription';

// === INTERFACES ===
interface IMultiFacetAutocompleteState {
  value: IMultiFacet | undefined;
  validationInfo: ValidationInfo;
}

interface IMultiFacetAutocompleteProps {
  id: string;
  serverValidationName?: string;
  labelText?: string;
  labelClassName?: string;
  labelPosition?: AutocompleteLabelPosition;
  placeholderText?: string;
  multiFacetTypes?: MultiFacetType[];
  required?: boolean;
  className?: string;
  noResultsText?: string;
  disabled?: boolean;
  historyProps?: IAutocompleteHistoryProps;
  filterLineResults?: (line: ILine) => boolean;
  state: IMultiFacetAutocompleteState;
  onSelect?: (item: IMultiFacet | undefined) => void;
  onKeyUp?: (() => void) | undefined;
  onFocus?: (() => void) | undefined;
}

// === SETTINGS ===
const maxNumberOfAutocompleteSuggestions = 20;

// === FUNCTIONS ===
function createResourceUrl(searchText: string): string {
  return `/api/multifacet-autocomplete?q=${searchText}`;
}

function hideFutureLines(line: ILine): boolean {
  return !!line.appliesFrom && line.appliesFrom <= DateTime.now();
}

function processReceivedData(data: unknown | undefined, multiFacetTypes: MultiFacetType[], q: string, filterResults?: (line: ILine) => boolean): IAutocompleteItem[] | undefined {
  if (!data || !q) {
    return undefined;
  }
  const businessModels = data as MultiFacetAutocompleteApiModel;

  // Map municipalities
  const municipalities = multiFacetTypes.includes(MultiFacetType.Municipality) ? businessModels.municipalities.map((m) => MultiFacetMunicipalityApiModel.mapToBusinessModel(m)) : [];
  // Map lines
  const lines = multiFacetTypes.includes(MultiFacetType.Line) ? businessModels.lines.map((line) => MultiFacetVersionedLineApiModel.mapToBusinessModel(line)) : [];
  const linesWithCap = VersionedLineHelper.flattenAndFilterLinesData(lines, q, maxNumberOfAutocompleteSuggestions, filterResults);

  const multiFacetItems: IMultiFacet[] = [];

  // Add municipalities to the multiFacetItems array
  municipalities.forEach((municipality) => {
    multiFacetItems.push({
      type: MultiFacetType.Municipality,
      line: undefined,
      municipality: municipality,
    });
  });

  // Add lines to the multiFacetItems array
  linesWithCap.forEach((line) => {
    multiFacetItems.push({
      type: MultiFacetType.Line,
      line: line,
      municipality: undefined,
    });
  });
  return multiFacetItems;
}

function createSuggestionElement(suggestionId: string, item: IAutocompleteItem, isActive: boolean, suggestionSource: AutocompleteSuggestionSource, onClick: () => void): JSX.Element {
  const autoCompleteItem = item as IMultiFacet;

  return (
    <li
      id={suggestionId}
      key={suggestionId}
      role="option"
      aria-selected={isActive}
      className={`autocomplete-result d-flex align-items-center py-2 px-2 m-0 w-100 border-x border-top ${isActive ? 'is-active' : ''}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
    >
      {(suggestionSource === AutocompleteSuggestionSource.history || suggestionSource === AutocompleteSuggestionSource.commonsearches) && (
        <span className="visually-hidden">
          {TranslationService.get(suggestionSource === AutocompleteSuggestionSource.history ? 'common.autocomplete.recentsearch' : 'common.autocomplete.commonsearch')}
        </span>
      )}
      {/* Line */}
      {autoCompleteItem.type === MultiFacetType.Line && autoCompleteItem.line && (
        <LineDescription line={autoCompleteItem.line} showTransportMode={true} showDirectionName={true} showAppliesFromDateOnFutureLines={true} />
      )}
      {/* Municipality */}
      {autoCompleteItem.type === MultiFacetType.Municipality && autoCompleteItem.municipality && <MunicipalityListShowcase municipalities={[autoCompleteItem.municipality]} />}
    </li>
  );
}

function MultiFacetAutocomplete({
  id,
  serverValidationName = undefined,
  labelText,
  labelClassName = undefined,
  labelPosition = AutocompleteLabelPosition.above,
  placeholderText,
  multiFacetTypes = [MultiFacetType.Line, MultiFacetType.Municipality],
  required = false,
  noResultsText,
  className = undefined,
  disabled = false,
  historyProps = undefined,
  filterLineResults = hideFutureLines,
  state,
  onSelect = () => {},
  onKeyUp,
  onFocus,
  ref, // Accept `ref` as a regular prop
}: IMultiFacetAutocompleteProps & { ref?: React.Ref<IAutocomplete> }) {
  // === STATE ===
  const [searchText, setSearchText] = useState<string>('');

  // === REFS ===
  const autocompleteRef = useRef<IAutocomplete>(null);

  // === IMPERATIVE HANDLE ===
  useImperativeHandle(ref, () => ({
    clear() {
      setSearchText('');
      autocompleteRef.current?.clear();
    },
    addSelectedItemToHistory(item: IAutocompleteItem) {
      autocompleteRef.current?.addSelectedItemToHistory(item);
    },
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${labelText} - `);
      ValidationEngine.for(updatedValidationInfo)
        .requiredIf(required, state.value !== undefined)
        .validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(serverValidationErrors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelText} - `,
        serverValidationErrors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
        {},
      );
    },
  }));

  return (
    <div>
      {labelPosition === AutocompleteLabelPosition.above && labelText && (
        <label id={`${id}-label`} htmlFor={id} className={labelClassName ?? ''}>
          {labelText}
          {required && <RequiredAsterisk />}
        </label>
      )}
      <Autocomplete
        selectedItem={state.value}
        searchText={searchText}
        id={id}
        ref={autocompleteRef} // Pass the internal ref to the Autocomplete component
        label={labelPosition === AutocompleteLabelPosition.inside ? labelText : undefined}
        placeholderText={placeholderText}
        required={required}
        noResultsText={noResultsText}
        className={className}
        disabled={disabled}
        historyProps={
          historyProps
            ? {
                ...historyProps,
                mapHistoryStringToItems: (s) => JSON.parse(s) as IMultiFacet[],
                mapItemsToHistoryString: (x) => JSON.stringify(x),
              }
            : undefined
        }
        isInvalid={state.validationInfo.isInvalid}
        ariaDescribedBy={state.validationInfo.isInvalid ? `${id}-error` : undefined}
        listAriaLabelledBy={labelPosition === AutocompleteLabelPosition.above ? `${id}-label` : undefined}
        createResourceUrl={createResourceUrl}
        processReceivedData={(data, q) => processReceivedData(data, multiFacetTypes, q, filterLineResults)}
        getSuggestionId={MultiFacetHelper.getSuggestionId}
        getItemId={MultiFacetHelper.getAutocompleteItemId}
        getDisplayName={MultiFacetHelper.getAutocompleteItemDisplayName}
        createSuggestionElement={createSuggestionElement}
        onSelect={(item: IAutocompleteItem | undefined) => {
          if (item !== undefined) {
            setSearchText('');
          }
          onSelect?.(item as IMultiFacet | undefined);
          onKeyUp?.();
        }}
        onSearchTextChange={(newSearchText: string) => {
          setSearchText(newSearchText);
          onKeyUp?.();
        }}
        onFocus={onFocus}
      />
      <div id={`${id}-error`} className={`invalid-feedback ${state.validationInfo.isInvalid ? 'd-block' : ''}`} aria-live="polite">
        {state.validationInfo.errors.map((error) => (
          <div key={error}>{error}</div>
        ))}
      </div>
    </div>
  );
}

export default MultiFacetAutocomplete;
